import React from 'react';
import { cn } from './_cn';

const variants = {
  default:
    'border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700',
  primary:
    'border border-transparent bg-blue-600 text-white shadow-sm hover:bg-blue-700 dark:bg-sky-500 dark:text-slate-900 dark:hover:bg-sky-400',
  danger:
    'border border-transparent bg-rose-600 text-white shadow-sm hover:bg-rose-700',
  ghost:
    'border border-transparent bg-transparent text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700',
  subtle:
    'border border-transparent bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600',
  link:
    'border border-transparent bg-transparent px-0 text-blue-600 hover:text-blue-700 hover:underline dark:text-sky-400 dark:hover:text-sky-300',
};

const sizes = {
  sm: 'h-8 px-3 text-xs gap-1',
  md: 'h-9 px-4 text-xs gap-1.5',
  lg: 'h-11 px-5 text-sm gap-2',
};

const Spinner = ({ large }) => (
  <svg
    className={cn('animate-spin', large ? 'h-4 w-4' : 'h-3.5 w-3.5')}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
  >
    <circle
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="3"
      className="opacity-25"
    />
    <path
      d="M4 12a8 8 0 018-8"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
    />
  </svg>
);

const TWButton = React.forwardRef(
  (
    {
      variant = 'default',
      size = 'md',
      icon: Icon,
      iconRight: IconRight,
      loading = false,
      disabled = false,
      type = 'button',
      pill = false,
      block = false,
      label,
      className = '',
      children,
      ...rest
    },
    ref,
  ) => {
    const iconCls = size === 'lg' ? 'h-4 w-4' : 'h-3.5 w-3.5';
    const content = children ?? label;
    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled || loading}
        className={cn(
          'inline-flex items-center justify-center font-semibold transition active:scale-[0.98]',
          'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 dark:focus:ring-sky-400 dark:focus:ring-offset-slate-900',
          'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100',
          pill ? 'rounded-full' : 'rounded-md',
          block ? 'w-full' : '',
          variants[variant] || variants.default,
          sizes[size] || sizes.md,
          className,
        )}
        {...rest}
      >
        {loading ? (
          <Spinner large={size === 'lg'} />
        ) : Icon ? (
          <Icon className={iconCls} aria-hidden />
        ) : null}
        {content}
        {IconRight && !loading ? (
          <IconRight className={iconCls} aria-hidden />
        ) : null}
      </button>
    );
  },
);

TWButton.displayName = 'TWButton';

export default TWButton;
